/**
 * Event Modal Component
 * Create and edit calendar events with full feature support
 */

import React, { useState, useEffect } from 'react';
import { format, addHours } from 'date-fns';
import { useUserTimezone } from '@/hooks/useUserTimezone';
import {
  formatClock,
  isValidTimeZone,
  toZonedInput,
  zonedInputToIso,
  zonedInputToMs,
  zonedNow,
  zoneAbbreviation,
  zonesDiffer,
} from '@/utils/zonedTime';
import { TimeZoneSelect } from '@/components/ui/TimeZoneSelect';
import {
  X,
  Calendar,
  Clock,
  MapPin,
  Video,
  Phone,
  Users,
  Bell,
  Repeat,
  Tag,
  Trash2,
  Save,
  Loader2,
  UserPlus,
  Mail,
  Search,
  ChevronDown,
  ChevronUp,
  Globe,
  AlertTriangle,
  Link,
} from 'lucide-react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import {
  calendarAPI,
  meetingAPI,
  CalendarEvent,
  CalendarEventCreate,
  CalendarEventUpdate,
  AttendeeCreate,
  EventType,
  LocationType,
  EventPriority,
  MeetingProvider,
  ReminderType,
  EVENT_TYPE_LABELS,
  LOCATION_TYPE_LABELS,
  MEETING_PROVIDER_LABELS,
  PRIORITY_LABELS,
  weaveInJoinPath,
} from '@/services/api/calendar';
import { telehealthSessionAPI } from '@/services/api/telehealth';
import MeetingEventPanel from './MeetingEventPanel';
import { oauthAPI } from '@/services/api/oauth';
import { cn } from '@/lib/utils';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { getWeaveinApiUrl } from '@/src/config';
import { authFetch } from '@/lib/authFetch';

// Helper function for attendee search
const searchMembers = async (query: string): Promise<{ id: number; name: string; email?: string }[]> => {
  const response = await authFetch(`${getWeaveinApiUrl('/api')}/members?search=${encodeURIComponent(query)}&limit=10`);
  if (!response.ok) return [];
  const data = await response.json();
  return (data.members || data || []).map((m: any) => ({
    id: m.id,
    name: `${m.first_name || ''} ${m.last_name || ''}`.trim() || `Member #${m.id}`,
    email: m.email
  }));
};

const searchUsers = async (query: string): Promise<{ id: number; name: string; email?: string }[]> => {
  const response = await authFetch(`${getWeaveinApiUrl('/api')}/users?search=${encodeURIComponent(query)}&limit=10`);
  if (!response.ok) return [];
  const data = await response.json();
  return (data.users || data || []).map((u: any) => ({
    id: u.id,
    name: u.full_name || u.name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email,
    email: u.email
  }));
};

// Attendee types
interface Attendee {
  id?: number;
  type: 'member' | 'user' | 'external';
  user_id?: number;
  member_id?: number;
  external_email?: string;
  external_name?: string;
  name: string;
  email?: string;
  attendee_role: 'required' | 'optional';
}

interface EventModalProps {
  open: boolean;
  onClose: () => void;
  event?: CalendarEvent | null;
  defaultDate?: Date;
  memberId?: number;
  memberName?: string;
  onSaved?: () => void;
}

export default function EventModal({
  open,
  onClose,
  event,
  defaultDate,
  memberId,
  memberName,
  onSaved,
}: EventModalProps) {
  const { t } = useAppTranslation();
  const queryClient = useQueryClient();
  const isEditing = !!event?.id;

  // TRUS-695: the user's selected zone (Settings > Preferences), else the
  // browser's. The date/time inputs are wall-clock time in formData.timezone
  // (this zone unless the user picks another) and are sent as UTC ISO strings.
  const userTimezone = useUserTimezone();

  // Check OAuth connection status for providers that need it
  const { data: oauthStatus } = useQuery({
    queryKey: ['oauth-status'],
    queryFn: oauthAPI.getStatus,
    staleTime: 60000, // Cache for 1 minute
  });

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_type: 'appointment' as EventType,
    start_time: '',
    end_time: '',
    all_day: false,
    timezone: userTimezone,
    location_type: 'in_person' as LocationType,
    location_address: '',
    meeting_provider: undefined as MeetingProvider | undefined,
    priority: 'normal' as EventPriority,
    is_private: false,
    create_meeting_link: false,
    member_id: memberId,
    create_telehealth_session: false,
    telehealth_duration: 30,
    telehealth_audio_only: false,
    telehealth_recording: false,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Attendee management state
  const [attendees, setAttendees] = useState<Attendee[]>([]);
  const [showAttendees, setShowAttendees] = useState(false);
  const [attendeeSearch, setAttendeeSearch] = useState('');
  const [attendeeSearchType, setAttendeeSearchType] = useState<'member' | 'user' | 'external'>('member');
  const [searchResults, setSearchResults] = useState<{ id: number; name: string; email?: string }[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [externalEmail, setExternalEmail] = useState('');
  const [externalName, setExternalName] = useState('');

  // TRUS-694: the providers this org may offer. WeaveIn video (twilio) always;
  // Jitsi only for a private JWT server (never the public meet.jit.si row).
  const { data: providerOptions } = useQuery({
    queryKey: ['meeting-provider-options'],
    queryFn: meetingAPI.getProviderOptions,
    staleTime: 60_000,
  });
  const offeredProviders = providerOptions?.options ?? [
    { value: 'twilio' as MeetingProvider, label: 'WeaveIn video', is_default: true },
  ];
  const defaultProvider: MeetingProvider = providerOptions?.default ?? 'twilio';

  // Invitees of an existing event (the list response has counts only).
  const { data: eventDetail } = useQuery({
    queryKey: ['calendar-event-detail', event?.id],
    queryFn: () => calendarAPI.getEvent(event!.id!),
    enabled: open && !!event?.id && !event?.meeting,
  });
  const existingInvitees = (eventDetail?.attendees ?? []).filter((a) => a.attendee_role !== 'organizer');

  // Initialize form when modal opens
  useEffect(() => {
    if (open) {
      if (event) {
        // Editing existing event. TRUS-697: its times are edited in the EVENT's
        // own zone (it may differ from the user's), else the user's.
        const eventZone = isValidTimeZone(event.timezone) ? event.timezone : userTimezone;
        setFormData({
          title: event.title || '',
          description: event.description || '',
          event_type: event.event_type || 'appointment',
          start_time: event.start_time ? toZonedInput(event.start_time, eventZone) : '',
          end_time: event.end_time ? toZonedInput(event.end_time, eventZone) : '',
          all_day: event.all_day || false,
          timezone: eventZone,
          location_type: event.location_type || 'in_person',
          location_address: event.location_address || '',
          meeting_provider: event.meeting_provider as MeetingProvider | undefined,
          priority: event.priority || 'normal',
          is_private: event.is_private || false,
          create_meeting_link: false,
          member_id: event.member_id || memberId,
          create_telehealth_session: false,
          telehealth_duration: 30,
          telehealth_audio_only: false,
          telehealth_recording: false,
        });
      } else {
        // New event - use user's preferred timezone
        // defaultDate is a calendar-grid slot: wall-clock time in the user's zone.
        const startDate = defaultDate || zonedNow(userTimezone);
        const endDate = addHours(startDate, 1);

        setFormData({
          title: '',
          description: '',
          event_type: 'appointment',
          start_time: format(startDate, "yyyy-MM-dd'T'HH:mm"),
          end_time: format(endDate, "yyyy-MM-dd'T'HH:mm"),
          all_day: false,
          timezone: userTimezone,
          location_type: 'in_person',
          location_address: '',
          meeting_provider: undefined,
          priority: 'normal',
          is_private: false,
          create_meeting_link: true,
          member_id: memberId,
          create_telehealth_session: false,
          telehealth_duration: 30,
          telehealth_audio_only: false,
          telehealth_recording: false,
        });
      }
      setErrors({});
      setAttendees([]);
      setAttendeeSearch('');
      setSearchResults([]);
      setExternalEmail('');
      setExternalName('');
    }
  }, [open, event, defaultDate, memberId]);

  // A new virtual / hybrid event starts on the org's default provider
  // (WeaveIn video unless the org chose another offered one).
  useEffect(() => {
    if (!open || isEditing) return;
    if (formData.location_type !== 'virtual' && formData.location_type !== 'hybrid') return;
    const offered = offeredProviders.some((o) => o.value === formData.meeting_provider);
    if (!formData.meeting_provider || !offered) {
      setFormData((prev) => ({ ...prev, meeting_provider: defaultProvider }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEditing, formData.location_type, defaultProvider, providerOptions]);

  // Attendee search effect
  useEffect(() => {
    if (!attendeeSearch || attendeeSearch.length < 2 || attendeeSearchType === 'external') {
      setSearchResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const results = attendeeSearchType === 'member'
          ? await searchMembers(attendeeSearch)
          : await searchUsers(attendeeSearch);
        // Filter out already added attendees
        const filtered = results.filter((r) => {
          return !attendees.some((a) =>
            (attendeeSearchType === 'member' && a.member_id === r.id) ||
            (attendeeSearchType === 'user' && a.user_id === r.id)
          );
        });
        setSearchResults(filtered);
      } catch (err) {
        console.error('Search error:', err);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [attendeeSearch, attendeeSearchType, attendees]);

  // Add attendee from search results
  const addAttendee = (result: { id: number; name: string; email?: string }) => {
    const newAttendee: Attendee = {
      type: attendeeSearchType as 'member' | 'user',
      name: result.name,
      email: result.email,
      attendee_role: 'required',
      ...(attendeeSearchType === 'member' ? { member_id: result.id } : { user_id: result.id }),
    };
    setAttendees((prev) => [...prev, newAttendee]);
    setAttendeeSearch('');
    setSearchResults([]);
  };

  // Add external attendee
  const addExternalAttendee = () => {
    if (!externalEmail || !externalEmail.includes('@')) return;
    const newAttendee: Attendee = {
      type: 'external',
      name: externalName || externalEmail.split('@')[0],
      email: externalEmail,
      external_email: externalEmail,
      external_name: externalName || undefined,
      attendee_role: 'required',
    };
    setAttendees((prev) => [...prev, newAttendee]);
    setExternalEmail('');
    setExternalName('');
  };

  // Remove attendee
  const removeAttendee = (index: number) => {
    setAttendees((prev) => prev.filter((_, i) => i !== index));
  };

  // Toggle attendee role
  const toggleAttendeeRole = (index: number) => {
    setAttendees((prev) =>
      prev.map((a, i) =>
        i === index ? { ...a, attendee_role: a.attendee_role === 'required' ? 'optional' : 'required' } : a
      )
    );
  };

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: CalendarEventCreate) => calendarAPI.createEvent(data),
    onSuccess: async () => {
      // Invalidate all calendar-related queries to ensure refresh
      await queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
      await queryClient.invalidateQueries({ queryKey: ['member-calendar-events'] });
      onSaved?.();
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: CalendarEventUpdate }) =>
      calendarAPI.updateEvent(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
      onSaved?.();
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => calendarAPI.deleteEvent(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
      onSaved?.();
    },
  });

  const isLoading = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  // Validation
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.start_time) {
      newErrors.start_time = 'Start time is required';
    }

    if (!formData.end_time) {
      newErrors.end_time = 'End time is required';
    }

    if (formData.start_time && formData.end_time && formData.start_time >= formData.end_time) {
      newErrors.end_time = 'End time must be after start time';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Form handlers
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    // Clear error on change
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    try {
      if (isEditing && event?.id) {
        await updateMutation.mutateAsync({
          id: event.id,
          data: {
            title: formData.title,
            description: formData.description || undefined,
            event_type: formData.event_type,
            start_time: zonedInputToIso(formData.start_time, formData.timezone) ?? formData.start_time,
            end_time: zonedInputToIso(formData.end_time, formData.timezone) ?? formData.end_time,
            timezone: formData.timezone,
            all_day: formData.all_day,
            location_type: formData.location_type,
            location_address: formData.location_address || undefined,
            meeting_provider: formData.meeting_provider,
            priority: formData.priority,
            is_private: formData.is_private,
          },
        });
      } else {
        // Convert attendees to AttendeeCreate format
        const attendeeData: AttendeeCreate[] = attendees.map((a) => ({
          user_id: a.user_id,
          member_id: a.member_id,
          external_email: a.external_email,
          external_name: a.external_name,
          attendee_role: a.attendee_role,
          is_optional: a.attendee_role === 'optional',
        }));

        const calendarEvent = await createMutation.mutateAsync({
          title: formData.title,
          description: formData.description || undefined,
          event_type: formData.event_type,
          start_time: zonedInputToIso(formData.start_time, formData.timezone) ?? formData.start_time,
          end_time: zonedInputToIso(formData.end_time, formData.timezone) ?? formData.end_time,
          all_day: formData.all_day,
          timezone: formData.timezone,
          location_type: formData.location_type,
          location_address: formData.location_address || undefined,
          meeting_provider: formData.meeting_provider,
          priority: formData.priority,
          is_private: formData.is_private,
          create_meeting_link: formData.create_meeting_link,
          member_id: formData.member_id,
          attendees: attendeeData.length > 0 ? attendeeData : undefined,
          allow_overlap: true,  // Allow overlapping events
        });

        // Create telehealth session if requested
        if (formData.create_telehealth_session && formData.member_id && calendarEvent.id) {
          try {
            // Calculate duration from start/end times
            const startMs = zonedInputToMs(formData.start_time, formData.timezone) ?? 0;
            const endMs = zonedInputToMs(formData.end_time, formData.timezone) ?? 0;
            const durationMinutes = Math.round((endMs - startMs) / 60000);

            await telehealthSessionAPI.createSession({
              calendar_event_id: calendarEvent.id,
              member_id: formData.member_id,
              scheduled_duration_minutes: durationMinutes,
              is_audio_only: formData.telehealth_audio_only,
              recording_enabled: formData.telehealth_recording,
            });
            queryClient.invalidateQueries({ queryKey: ['telehealth-upcoming-sessions'] });
          } catch (telehealthError) {
            console.error('Failed to create telehealth session:', telehealthError);
            // Don't fail the whole operation if telehealth session fails
          }
        }
      }
    } catch (error) {
      console.error('Error saving event:', error);
    }
  };

  const handleDelete = async () => {
    if (!event?.id || !confirm('Are you sure you want to delete this event?')) return;

    try {
      await deleteMutation.mutateAsync(event.id);
    } catch (error) {
      console.error('Error deleting event:', error);
    }
  };

  if (!open) return null;

  // A WeaveIn meeting's entry: read-only, edited from the meeting card (TRUS-694).
  if (event?.meeting) {
    return <MeetingEventPanel event={event} onClose={onClose} />;
  }

  const locationIcon = {
    in_person: MapPin,
    virtual: Video,
    phone: Phone,
    hybrid: Users,
  };

  const LocationIcon = locationIcon[formData.location_type];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto mx-4">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {isEditing ? 'Edit Event' : 'New Event'}
            {memberName && (
              <span className="text-sm font-normal text-gray-500 ml-2">
                for {memberName}
              </span>
            )}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Title *
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              className={cn(
                'w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 dark:bg-gray-700 dark:border-gray-600',
                errors.title && 'border-red-500'
              )}
              placeholder="Event title"
            />
            {errors.title && (
              <p className="text-sm text-red-500 mt-1">{errors.title}</p>
            )}
          </div>

          {/* Event Type & Priority */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Event Type
              </label>
              <select
                name="event_type"
                value={formData.event_type}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 dark:bg-gray-700 dark:border-gray-600"
              >
                {Object.entries(EVENT_TYPE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Priority
              </label>
              <select
                name="priority"
                value={formData.priority}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 dark:bg-gray-700 dark:border-gray-600"
              >
                {Object.entries(PRIORITY_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Date/Time */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <Clock className="h-4 w-4 inline mr-1" />
                Start *
              </label>
              <input
                type="datetime-local"
                name="start_time"
                value={formData.start_time}
                onChange={handleInputChange}
                className={cn(
                  'w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 dark:bg-gray-700 dark:border-gray-600',
                  errors.start_time && 'border-red-500'
                )}
              />
              {errors.start_time && (
                <p className="text-sm text-red-500 mt-1">{errors.start_time}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <Clock className="h-4 w-4 inline mr-1" />
                End *
              </label>
              <input
                type="datetime-local"
                name="end_time"
                value={formData.end_time}
                onChange={handleInputChange}
                className={cn(
                  'w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 dark:bg-gray-700 dark:border-gray-600',
                  errors.end_time && 'border-red-500'
                )}
              />
              {errors.end_time && (
                <p className="text-sm text-red-500 mt-1">{errors.end_time}</p>
              )}
            </div>
          </div>

          {/* All Day */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="all_day"
              name="all_day"
              checked={formData.all_day}
              onChange={handleInputChange}
              className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
            />
            <label
              htmlFor="all_day"
              className="ml-2 text-sm text-gray-700 dark:text-gray-300"
            >
              All day event
            </label>
          </div>

          {/* Time zone (TRUS-697): the event's own zone, default the user's. */}
          <div>
            <label
              htmlFor="event-timezone-select"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              <Globe className="h-4 w-4 inline mr-1" />
              {t('time.zone.label', 'Time zone')}
            </label>
            <TimeZoneSelect
              id="event-timezone-select"
              name="timezone"
              value={formData.timezone}
              onChange={(tz) => setFormData((prev) => ({ ...prev, timezone: tz }))}
              userTimeZone={userTimezone}
              at={zonedInputToMs(formData.start_time, formData.timezone) ?? undefined}
              data-testid="event-timezone"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-testid="event-timezone-note">
              {t('calendar.eventTimesInZone', 'The start and end times are in {{zone}} ({{abbr}}).', {
                zone: formData.timezone.replace(/_/g, ' '),
                abbr: zoneAbbreviation(zonedInputToMs(formData.start_time, formData.timezone) ?? Date.now(), formData.timezone),
              })}
              {(() => {
                const startMs = zonedInputToMs(formData.start_time, formData.timezone);
                if (startMs == null || !zonesDiffer(startMs, userTimezone, formData.timezone)) return null;
                return (
                  <span data-testid="event-timezone-yours">
                    {' '}
                    {t('time.zone.thatIsForYou', 'That is {{time}} in your time zone.', {
                      time: formatClock(startMs, userTimezone),
                    })}
                  </span>
                );
              })()}
            </p>
          </div>

          {/* Location Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <LocationIcon className="h-4 w-4 inline mr-1" />
              Location Type
            </label>
            <div className="grid grid-cols-4 gap-2">
              {Object.entries(LOCATION_TYPE_LABELS).map(([value, label]) => {
                const Icon = locationIcon[value as LocationType];
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() =>
                      setFormData((prev) => ({ ...prev, location_type: value as LocationType }))
                    }
                    className={cn(
                      'flex flex-col items-center gap-1 p-3 rounded-lg border transition-colors',
                      formData.location_type === value
                        ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-300'
                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                    )}
                  >
                    <Icon className="h-5 w-5" />
                    <span className="text-xs">{label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Location Address (for in-person) */}
          {(formData.location_type === 'in_person' || formData.location_type === 'hybrid') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Address
              </label>
              <input
                type="text"
                name="location_address"
                value={formData.location_address}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 dark:bg-gray-700 dark:border-gray-600"
                placeholder="Enter address"
              />
            </div>
          )}

          {/* Meeting Provider (for virtual) */}
          {(formData.location_type === 'virtual' || formData.location_type === 'hybrid') && (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Video Conference Provider
                </label>
                <select
                  name="meeting_provider"
                  value={formData.meeting_provider || ''}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 dark:bg-gray-700 dark:border-gray-600"
                >
                  {/* An existing event keeps the provider it has, even one no longer offered. */}
                  {isEditing && formData.meeting_provider && !offeredProviders.some((o) => o.value === formData.meeting_provider) && (
                    <option value={formData.meeting_provider}>
                      {MEETING_PROVIDER_LABELS[formData.meeting_provider] ?? formData.meeting_provider}
                    </option>
                  )}
                  {isEditing && !formData.meeting_provider && (
                    <option value="">{t('calendar.providers.none', 'No provider')}</option>
                  )}
                  {offeredProviders.map((option) => (
                    <option key={option.value} value={option.value}>
                      {t(`calendar.providers.${option.value}`, option.label)}
                      {option.is_default ? ` ${t('calendar.providers.defaultSuffix', '(Default)')}` : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* OAuth Connection Warning */}
              {formData.meeting_provider === 'zoom' && !oauthStatus?.zoom?.connected && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                      Zoom account not connected
                    </p>
                    <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                      Connect your Zoom account in{' '}
                      <a href="/settings" className="underline font-medium hover:text-amber-900">
                        Settings → Connected Services
                      </a>{' '}
                      to automatically create Zoom meeting links.
                    </p>
                  </div>
                </div>
              )}
              {formData.meeting_provider === 'google_meet' && !oauthStatus?.google?.connected && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                      Google account not connected
                    </p>
                    <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                      Connect your Google account in{' '}
                      <a href="/settings" className="underline font-medium hover:text-amber-900">
                        Settings → Connected Services
                      </a>{' '}
                      to create Google Meet links.
                    </p>
                  </div>
                </div>
              )}
              {formData.meeting_provider === 'teams' && !oauthStatus?.microsoft?.connected && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                      Microsoft account not connected
                    </p>
                    <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                      Connect your Microsoft account in{' '}
                      <a href="/settings" className="underline font-medium hover:text-amber-900">
                        Settings → Connected Services
                      </a>{' '}
                      to create Teams meeting links.
                    </p>
                  </div>
                </div>
              )}
              {formData.meeting_provider === 'twilio' && (
                <div
                  className="flex items-start gap-2 p-3 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-lg"
                  data-testid="event-weavein-video-note"
                >
                  <Link className="h-5 w-5 text-teal-600 dark:text-teal-400 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-teal-700 dark:text-teal-300">
                    {t(
                      'calendar.providers.twilioNote',
                      'A private WeaveIn video link is created for this event. Only invited people can join.',
                    )}
                  </p>
                </div>
              )}

              {!isEditing && (
                <div className="space-y-3">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="create_meeting_link"
                      name="create_meeting_link"
                      checked={formData.create_meeting_link}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                    />
                    <label
                      htmlFor="create_meeting_link"
                      className="ml-2 text-sm text-gray-700 dark:text-gray-300"
                    >
                      Generate meeting link automatically
                    </label>
                  </div>

                  {/* Telehealth Session Option */}
                  {formData.member_id && (
                    <div className="space-y-3">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="create_telehealth_session"
                          name="create_telehealth_session"
                          checked={formData.create_telehealth_session}
                          onChange={handleInputChange}
                          className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                        />
                        <label
                          htmlFor="create_telehealth_session"
                          className="ml-2 text-sm text-gray-700 dark:text-gray-300"
                        >
                          Create telehealth session (enables encounter documentation)
                        </label>
                      </div>

                      {formData.create_telehealth_session && (
                        <div className="ml-6 p-3 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-lg space-y-3">
                          <p className="text-sm text-teal-700 dark:text-teal-300 font-medium flex items-center gap-2">
                            <Video className="h-4 w-4" />
                            Telehealth Session Settings
                          </p>
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              id="telehealth_audio_only"
                              name="telehealth_audio_only"
                              checked={formData.telehealth_audio_only}
                              onChange={handleInputChange}
                              className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                            />
                            <label
                              htmlFor="telehealth_audio_only"
                              className="ml-2 text-sm text-gray-600 dark:text-gray-400"
                            >
                              Audio only (no video)
                            </label>
                          </div>
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              id="telehealth_recording"
                              name="telehealth_recording"
                              checked={formData.telehealth_recording}
                              onChange={handleInputChange}
                              className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                            />
                            <label
                              htmlFor="telehealth_recording"
                              className="ml-2 text-sm text-gray-600 dark:text-gray-400"
                            >
                              Enable session recording (requires consent)
                            </label>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Show existing meeting link */}
              {event?.meeting_link && (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Meeting Link:</p>
                  {weaveInJoinPath(event.meeting_link) ? (
                    // TRUS-688: WeaveIn in-app video. Open it in this app (its own
                    // host), not whatever host the link was minted with.
                    <a
                      href={weaveInJoinPath(event.meeting_link) ?? undefined}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-teal-600 hover:underline text-sm break-all"
                    >
                      {t('calendar.visitJoin.joinInWeaveIn', 'Join in WeaveIn')}
                    </a>
                  ) : (
                    <a
                      href={event.meeting_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-teal-600 hover:underline text-sm break-all"
                    >
                      {event.meeting_link}
                    </a>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 dark:bg-gray-700 dark:border-gray-600"
              placeholder="Event details..."
            />
          </div>

          {/* Invitees of an existing event (TRUS-694: they were never shown) */}
          {isEditing && existingInvitees.length > 0 && (
            <div data-testid="event-invitees">
              <p className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <Users className="h-4 w-4" aria-hidden="true" />
                {t('calendar.meetingEvent.invitees', 'Invitees')} ({existingInvitees.length})
              </p>
              <ul className="space-y-1">
                {existingInvitees.map((a, i) => (
                  <li
                    key={a.id ?? i}
                    className="flex items-center justify-between gap-3 rounded-lg bg-gray-50 px-3 py-2 text-sm dark:bg-gray-700/50"
                  >
                    <span className="min-w-0 truncate dark:text-white">{a.attendee_name || a.attendee_email}</span>
                    <span className="flex-shrink-0 text-xs text-gray-500 dark:text-gray-400">{a.response_status}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Attendees Section */}
          {!isEditing && (
            <div className="border rounded-lg overflow-hidden">
              {/* Header - Collapsible */}
              <button
                type="button"
                onClick={() => setShowAttendees(!showAttendees)}
                className="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <UserPlus className="h-5 w-5 text-teal-600" />
                  <span className="font-medium text-gray-900 dark:text-white">
                    Invite Attendees
                  </span>
                  {attendees.length > 0 && (
                    <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-teal-100 dark:bg-teal-900 text-teal-700 dark:text-teal-300 rounded-full">
                      {attendees.length}
                    </span>
                  )}
                </div>
                {showAttendees ? (
                  <ChevronUp className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                )}
              </button>

              {/* Attendee Content */}
              {showAttendees && (
                <div className="p-4 space-y-4 border-t">
                  {/* Search Type Tabs */}
                  <div className="flex border-b">
                    {[
                      { id: 'member', label: 'Members', icon: Users },
                      { id: 'user', label: 'Staff', icon: UserPlus },
                      { id: 'external', label: 'External', icon: Mail },
                    ].map((tab) => (
                      <button
                        key={tab.id}
                        type="button"
                        onClick={() => {
                          setAttendeeSearchType(tab.id as 'member' | 'user' | 'external');
                          setAttendeeSearch('');
                          setSearchResults([]);
                        }}
                        className={cn(
                          'flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px',
                          attendeeSearchType === tab.id
                            ? 'border-teal-500 text-teal-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                        )}
                      >
                        <tab.icon className="h-4 w-4" />
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  {/* Search Input (for members/users) */}
                  {attendeeSearchType !== 'external' && (
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        value={attendeeSearch}
                        onChange={(e) => setAttendeeSearch(e.target.value)}
                        placeholder={`Search ${attendeeSearchType === 'member' ? 'members' : 'staff'}...`}
                        className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 dark:bg-gray-700 dark:border-gray-600"
                      />
                      {isSearching && (
                        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 animate-spin" />
                      )}
                    </div>
                  )}

                  {/* Search Results Dropdown */}
                  {searchResults.length > 0 && (
                    <div className="border rounded-lg max-h-40 overflow-y-auto">
                      {searchResults.map((result) => (
                        <button
                          key={result.id}
                          type="button"
                          onClick={() => addAttendee(result)}
                          className="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 text-left"
                        >
                          <div className="w-8 h-8 rounded-full bg-teal-100 dark:bg-teal-900 flex items-center justify-center text-teal-600 dark:text-teal-400 text-sm font-medium">
                            {result.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {result.name}
                            </p>
                            {result.email && (
                              <p className="text-xs text-gray-500 truncate">{result.email}</p>
                            )}
                          </div>
                          <UserPlus className="h-4 w-4 text-teal-600" />
                        </button>
                      ))}
                    </div>
                  )}

                  {/* External Email Form */}
                  {attendeeSearchType === 'external' && (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Email Address *
                        </label>
                        <input
                          type="email"
                          value={externalEmail}
                          onChange={(e) => setExternalEmail(e.target.value)}
                          placeholder="participant@example.com"
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 dark:bg-gray-700 dark:border-gray-600"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Name (optional)
                        </label>
                        <input
                          type="text"
                          value={externalName}
                          onChange={(e) => setExternalName(e.target.value)}
                          placeholder="John Doe"
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 dark:bg-gray-700 dark:border-gray-600"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={addExternalAttendee}
                        disabled={!externalEmail || !externalEmail.includes('@')}
                        className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <UserPlus className="h-4 w-4" />
                        Add External Participant
                      </button>
                    </div>
                  )}

                  {/* Added Attendees List */}
                  {attendees.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Added Attendees ({attendees.length})
                      </p>
                      <div className="space-y-2">
                        {attendees.map((attendee, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                          >
                            <div className="flex items-center gap-3">
                              <div className={cn(
                                'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium',
                                attendee.type === 'member'
                                  ? 'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400'
                                  : attendee.type === 'user'
                                  ? 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400'
                                  : 'bg-purple-100 dark:bg-purple-900 text-purple-600 dark:text-purple-400'
                              )}>
                                {attendee.name.charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900 dark:text-white">
                                  {attendee.name}
                                </p>
                                <div className="flex items-center gap-2 text-xs text-gray-500">
                                  <span className={cn(
                                    'px-1.5 py-0.5 rounded',
                                    attendee.type === 'member'
                                      ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400'
                                      : attendee.type === 'user'
                                      ? 'bg-green-100 dark:bg-green-900/50 text-green-600 dark:text-green-400'
                                      : 'bg-purple-100 dark:bg-purple-900/50 text-purple-600 dark:text-purple-400'
                                  )}>
                                    {attendee.type === 'member' ? 'Member' : attendee.type === 'user' ? 'Staff' : 'External'}
                                  </span>
                                  {attendee.email && <span>{attendee.email}</span>}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => toggleAttendeeRole(index)}
                                className={cn(
                                  'text-xs px-2 py-1 rounded transition-colors',
                                  attendee.attendee_role === 'required'
                                    ? 'bg-teal-100 dark:bg-teal-900/50 text-teal-700 dark:text-teal-300'
                                    : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
                                )}
                              >
                                {attendee.attendee_role === 'required' ? 'Required' : 'Optional'}
                              </button>
                              <button
                                type="button"
                                onClick={() => removeAttendee(index)}
                                className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Private */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="is_private"
              name="is_private"
              checked={formData.is_private}
              onChange={handleInputChange}
              className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
            />
            <label
              htmlFor="is_private"
              className="ml-2 text-sm text-gray-700 dark:text-gray-300"
            >
              Private event (hide details from others)
            </label>
          </div>

          {/* Attendee & Reminder Info */}
          {event && (
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-2">
              {event.attendee_count && event.attendee_count > 0 && (
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <Users className="h-4 w-4" />
                  <span>
                    {event.confirmed_count} of {event.attendee_count} attendees confirmed
                  </span>
                </div>
              )}
              {event.member_name && (
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <Calendar className="h-4 w-4" />
                  <span>Appointment with {event.member_name}</span>
                </div>
              )}
            </div>
          )}

          {/* Notification Info */}
          {!isEditing && (attendees.length > 0 || memberId) && (
            <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <Mail className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800 dark:text-blue-300">
                <p className="font-medium">Invitations will be sent</p>
                <p className="text-blue-600 dark:text-blue-400">
                  {attendees.length > 0 && `${attendees.length} attendee${attendees.length > 1 ? 's' : ''}`}
                  {attendees.length > 0 && memberId && ' and '}
                  {memberId && 'the member'}
                  {' will receive email notifications with meeting details.'}
                </p>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between pt-4 border-t">
            {isEditing && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={isLoading}
                className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </button>
            )}
            <div className={cn('flex items-center gap-3', !isEditing && 'ml-auto')}>
              <button
                type="button"
                onClick={onClose}
                disabled={isLoading}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50"
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                {isEditing ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
